import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { loader } from 'graphql.macro';
import { useCurrentUser } from './UserProvider';
import CustomSpinner from '../components/CustomSpinner';
import { useOnlineStatus } from '../hooks/offline-hooks/offline-misc';

const headsQuery = loader('./MyInspections.heads.graphql');
const detailQuery = loader(
  '../pages/inspection_page/Inspection.inspection.graphql',
);

const CHUNK_SIZE = 5;
const DEBOUNCE_MS = 50;

function chunkArray(arr, size) {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

const generateSingleIdOptions = (id) => ({
  filters: [
    {
      field: 'id',
      value: `${id}`,
      operator: 'eq',
    },
    {
      field: 'isArchived',
      operator: 'eq',
      value: ['FALSE'],
    },
  ],
});

const MyInspectionsProviderContext = createContext();

export const MyInspectionsProvider = ({ children }) => {
  const { user, syncing } = useCurrentUser();
  const client = useApolloClient();

  const isOnline = useOnlineStatus();
  const [myInspections, setMyInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  const { data: headsData, refetch: refetchHeads } = useQuery(headsQuery, {
    skip: syncing,
    fetchPolicy: isOnline ? 'network-only' : 'cache-only',
    variables: {
      options: {
        filters: [
          { field: 'isArchived', operator: 'eq', value: ['FALSE'] },
          { field: 'creatorId', operator: 'eq', value: [`${user.id}`] },
        ],
        sort: [{ field: 'dateModified', order: 'desc' }],
        pageSize: 20,
      },
    },
  });

  const sync = useCallback(async () => {
    const heads = headsData?.inspections || [];
    if (!heads.length) {
      setMyInspections([]);
      setProgress(100);
      setLoading(false);
      return;
    }

    setLoading(true);
    setProgress(0);

    const idsToFetch = [];
    for (const h of heads) {
      let cached;
      try {
        cached = client.readQuery({
          query: detailQuery,
          variables: {
            options: generateSingleIdOptions(h.id),
          },
        }).inspections[0];
      } catch (e) {}
      if (!cached || cached.dateModified !== h.dateModified) {
        idsToFetch.push(h.id);
      }
    }

    const totalToFetch = idsToFetch.length;
    if (totalToFetch === 0) {
      const finalList = heads
        .map((h) => {
          try {
            return client.readQuery({
              query: detailQuery,
              variables: {
                options: generateSingleIdOptions(h.id),
              },
            }).inspections[0];
          } catch (e) {
            return null;
          }
        })
        .filter(Boolean);
      setMyInspections(finalList);
      setProgress(100);
      setLoading(false);
      return;
    }

    let processedCount = 0;
    const batches = chunkArray(idsToFetch, CHUNK_SIZE);
    for (let i = 0; i < batches.length; i++) {
      await Promise.all(
        batches[i].map((id) =>
          client
            .query({
              query: detailQuery,
              variables: {
                options: generateSingleIdOptions(id),
              },
              fetchPolicy: 'network-only',
            })
            .then((res) =>
              client.writeQuery({
                query: detailQuery,
                variables: {
                  options: generateSingleIdOptions(id),
                },
                data: { inspections: res.data.inspections },
              }),
            )
            .catch((err) => {
              console.error('Failed to load inspection', id, err);
            }),
        ),
      );
      processedCount += batches[i].length;
      const newProgress = Math.round((processedCount / totalToFetch) * 100);
      setProgress(newProgress);
      if (i < batches.length - 1) {
        await new Promise((r) => setTimeout(r, DEBOUNCE_MS));
      }
    }

    const finalList = heads
      .map((h) => {
        try {
          return client.readQuery({
            query: detailQuery,
            variables: {
              options: generateSingleIdOptions(h.id),
            },
          }).inspections[0];
        } catch (e) {
          return null;
        }
      })
      .filter(Boolean);

    setMyInspections(finalList);
    setProgress(100);
    setLoading(false);
  }, [headsData, client]);

  useEffect(() => {
    sync();
  }, [sync]);

  const syncFromCache = useCallback(() => {
    const heads = headsData?.inspections || [];
    if (!heads.length) return;
    const finalList = heads
      .map((h) => {
        try {
          return client.readQuery({
            query: detailQuery,
            variables: { options: generateSingleIdOptions(h.id) },
          }).inspections[0];
        } catch (e) {
          return null;
        }
      })
      .filter(Boolean);
    if (finalList.length > 0) {
      setMyInspections(finalList);
    }
  }, [headsData, client]);

  const refetchMyInspections = useCallback(() => {
    refetchHeads();
  }, [refetchHeads]);

  return (
    <MyInspectionsProviderContext.Provider
      value={{
        myInspections,
        refetchMyInspections,
        syncFromCache,
        loading,
      }}
    >
      {loading && !syncing && (
        <CustomSpinner text={`Fetching Recent Assessments... ${progress}%`} />
      )}
      {children}
    </MyInspectionsProviderContext.Provider>
  );
};

export const useMyInspections = () => {
  const ctx = useContext(MyInspectionsProviderContext);
  return { ...ctx, syncFromCache: ctx?.syncFromCache ?? (() => {}) };
};
